import type { NightLightSample } from "../types";

const LAYER_DATE = "2016-01-01";
const SAMPLE_ZOOM = 7;

function latLngToTile(lat: number, lng: number, z: number) {
  const n = 2 ** z;
  const x = ((lng + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const y =
    (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n;
  return { x, y, z };
}

export function blackMarbleUrl(z: number, x: number, y: number): string {
  return `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/VIIRS_Black_Marble/default/${LAYER_DATE}/GoogleMapsCompatible_Level8/${z}/${y}/${x}.png`;
}

export const BLACK_MARBLE_TEMPLATE =
  `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/VIIRS_Black_Marble/default/${LAYER_DATE}/GoogleMapsCompatible_Level8/{z}/{y}/{x}.png`;

function labelFor(luminance: number): NightLightSample {
  if (luminance <= 22) {
    return {
      luminance,
      label: "Very dark",
      detail: "Little satellite night light here. A promising dark-sky patch — confirm on the ground.",
    };
  }
  if (luminance <= 32) {
    return {
      luminance,
      label: "Dark",
      detail: "Low night lights nearby. Often good once you get away from the nearest town glow.",
    };
  }
  if (luminance <= 55) {
    return {
      luminance,
      label: "Transition",
      detail: "Some skyglow likely. Usable, but a curated pin farther out will usually be better.",
    };
  }
  if (luminance <= 130) {
    return {
      luminance,
      label: "Town glow",
      detail: "Satellite lights show a town or corridor. Fine for a stop, not a dark-sky night.",
    };
  }
  return {
    luminance,
    label: "Bright",
    detail: "Strong night lights. This is city or suburban sky.",
  };
}

export async function sampleNightLights(
  lat: number,
  lng: number,
): Promise<NightLightSample> {
  const { x, y, z } = latLngToTile(lat, lng, SAMPLE_ZOOM);
  const tileX = Math.floor(x);
  const tileY = Math.floor(y);
  const res = await fetch(blackMarbleUrl(z, tileX, tileY));
  if (!res.ok) {
    throw new Error("Night-light sample failed");
  }
  const blob = await res.blob();
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(bitmap, 0, 0);
  const px = Math.min(bitmap.width - 1, Math.max(0, Math.floor((x - tileX) * bitmap.width)));
  const py = Math.min(bitmap.height - 1, Math.max(0, Math.floor((y - tileY) * bitmap.height)));

  let sum = 0;
  let count = 0;
  for (let dx = -2; dx <= 2; dx += 1) {
    for (let dy = -2; dy <= 2; dy += 1) {
      const xx = Math.min(bitmap.width - 1, Math.max(0, px + dx));
      const yy = Math.min(bitmap.height - 1, Math.max(0, py + dy));
      const [r, g, b] = ctx.getImageData(xx, yy, 1, 1).data;
      sum += 0.2126 * r + 0.7152 * g + 0.0722 * b;
      count += 1;
    }
  }
  return labelFor(sum / count);
}
